import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import UserIcon from '../../../../assets/icons/user';
import AppCheckbox from '../../../../modules/app-checkbox/app-checkbox';
import IconBtn from '../../../../modules/icon-btn/icon-btn';
import UiInput from '../../../../modules/ui/ui-input/ui-input';
import { useAppDispatch } from '../../../../store';
import { updateUser } from '../../../../store/actions/auth';
import { RootState } from '../../../../types';
import ChangePasswordModal from '../change-password-modal';
import './index.scss';

type UserInfoFormState = {
  fullName: string;
  email: string;
  phoneNumber: string;
  enableOrderNotification: boolean;
  enableOnlyConfirmedNotification: boolean;
};

const ProfileUserForm = () => {
  const dispatch = useAppDispatch();
  const profile = useSelector((state: RootState) => state.auth.profile);

  const [readOnly, setReadOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isOpenModal, setOpenModal] = useState(false);

  const initialState: UserInfoFormState = {
    fullName: profile.fullName,
    email: profile.email,
    phoneNumber: profile.phoneNumber,
    enableOrderNotification: !!profile.enableOrderNotification,
    enableOnlyConfirmedNotification: !!profile.enableOnlyConfirmedNotification,
  };

  const formState = useFormik({
    initialValues: initialState,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: (values) => {
      if (values.email && values.fullName) {
        setLoading(true);
        dispatch(updateUser({ ...values })).then(
          (res) => {
            if (res.payload) {
              setLoading(false);
              setReadOnly(true);
            }
          },
          () => {
            setLoading(false);
            setReadOnly(false);
          }
        );
      }
    },
    validate: (values: Partial<UserInfoFormState>) => {
      const errors: Record<string, string> = {};

      if (values.fullName && values.fullName.trim().split(' ').length !== 3) {
        errors.fullName = `Введите корректное ФИО`;
      }

      if (values.email && !/\S+@\S+\.\S+/.test(values.email)) {
        errors.email = `Email введен неверно`;
      }

      return errors;
    },
  });

  const setInitChanges = () => {
    formState.setValues(initialState);
  };

  useEffect(() => setInitChanges(), [profile]);

  const isEditable =
    formState.values.email !== profile?.email ||
    formState.values.fullName !== profile?.fullName ||
    formState.values.enableOnlyConfirmedNotification !==
      !!profile?.enableOnlyConfirmedNotification ||
    formState.values.enableOrderNotification !==
      !!profile?.enableOrderNotification;

  const getFieldError = (field: keyof UserInfoFormState) =>
    formState.errors[field] && isEditable ? (
      <div className="form-item__footer red">{formState.errors[field]}</div>
    ) : null;

  const handleCancelChanges = () => {
    setInitChanges();
    setReadOnly(true);
  };

  return (
    <form className="form-profile-company form-profile-user">
      <div className="input-container">
        <UiInput
          value={formState.values.fullName}
          error={formState.errors.fullName}
          onChange={formState.handleChange}
          onInputBlur={formState.handleBlur}
          label="ФИО"
          readOnly={readOnly}
          name="fullName"
          lg
        />
        {getFieldError('fullName')}
      </div>
      <div className="input-container">
        <UiInput
          value={formState.values.phoneNumber}
          label="Телефон"
          readOnly
          name="phoneNumber"
          lg
        />
      </div>
      <div className="input-container">
        <UiInput
          name="email"
          type="email"
          value={formState.values.email}
          error={formState.errors.email}
          onChange={formState.handleChange}
          onInputBlur={formState.handleBlur}
          lg
          readOnly={readOnly}
          label="Email"
        />
        {getFieldError('email')}
      </div>

      <div className="checkbox-container">
        <AppCheckbox
          disabled={loading || readOnly}
          checked={profile.enableOrderNotification}
          title="Уведомлять на email о новых заказах"
          onChangeValue={(val) =>
            formState.setFieldValue('enableOrderNotification', val)
          }
        />
      </div>

      <div className="checkbox-container">
        <AppCheckbox
          disabled={loading || readOnly}
          checked={profile.enableOnlyConfirmedNotification}
          title="Уведомлять на email согласованиях контрагентами"
          onChangeValue={(val) =>
            formState.setFieldValue('enableOnlyConfirmedNotification', val)
          }
        />
      </div>

      <div className="checkbox-container">
        <IconBtn
          title="Изменить пароль"
          outlined
          sm
          icon={<UserIcon />}
          onClick={() => setOpenModal(true)}
        />
      </div>
      <div className="buttons">
        {readOnly && (
          <IconBtn
            title={'Редактировать'}
            onClick={() => setReadOnly(!readOnly)}
          />
        )}
        {!readOnly && isEditable && (
          <>
            <IconBtn
              disabled={
                loading ||
                !formState.isValid ||
                (!formState.values.email && !formState.values.fullName)
              }
              title={'Сохранить изменения'}
              onClick={formState.handleSubmit}
            />
            <IconBtn
              title="Отменить"
              outlined
              onClick={handleCancelChanges}
            />
          </>
        )}

        <ChangePasswordModal
          isOpenModal={isOpenModal}
          setOpenModal={setOpenModal}
        />
      </div>
    </form>
  );
};

export default ProfileUserForm;
